import {
  AlertCircle,
  Eye,
  FileText,
  Loader2,
  Paperclip,
  RotateCcw,
  Save,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  type SystemEmailAttachment,
  useGetSystemEmailTemplate,
  usePreviewSystemEmailTemplate,
  useResetSystemEmailTemplate,
  useUpsertSystemEmailTemplate,
} from '~entities/settings/email-settings/api/system-email-template';

import {
  TextEditor,
  type TextEditorRef,
} from '~pages/settings/email-form-config/text-editor';

import { showEmailTemplateSaveError } from './email-save-error-toast';

import { getErrorMessage } from '~shared/utils/error-handler';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { ScrollArea } from '~shared/shadcn/ui/scroll-area';
import { Separator } from '~shared/shadcn/ui/separator';

interface EmailTemplateEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  emailType: string;
  scenarioName: string;
}

type FocusTarget = 'subject' | 'body';

const isBlankEmailHtml = (html: string | undefined | null) => {
  const value = String(html ?? '');
  if (/<(img|table|hr|svg|video|audio|iframe)\b/i.test(value)) {
    return false;
  }

  const text = value
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, '')
    .trim();

  return text.length === 0;
};

export const EmailTemplateEditorDialog = ({
  open,
  onOpenChange,
  emailType,
  scenarioName,
}: EmailTemplateEditorDialogProps) => {
  const {
    data: template,
    isLoading,
    isError,
    refetch,
  } = useGetSystemEmailTemplate(emailType, open);

  const { mutateAsync: upsertTemplate, isPending: isSaving } =
    useUpsertSystemEmailTemplate();
  const { mutateAsync: resetTemplate, isPending: isResetting } =
    useResetSystemEmailTemplate();
  const { mutateAsync: previewTemplate, isPending: isPreviewing } =
    usePreviewSystemEmailTemplate();

  const [subjectLine, setSubjectLine] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  /** TipTap only reads `content` on mount — remount when this revision changes. */
  const [bodyEditorRevision, setBodyEditorRevision] = useState(0);
  const [bodyEditorSeed, setBodyEditorSeed] = useState<string | null>(null);
  const [keptAttachments, setKeptAttachments] = useState<
    SystemEmailAttachment[]
  >([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const bodyEditorRef = useRef<TextEditorRef>(null);
  const lastFocusedFieldRef = useRef<FocusTarget>('body');
  const subjectSelectionRef = useRef({ start: 0, end: 0 });

  useEffect(() => {
    if (!open) {
      setBodyEditorSeed(null);
      return;
    }
    if (!template) return;

    setSubjectLine(template.subjectLine);
    setBodyHtml(template.bodyHtml);
    setBodyEditorSeed(template.bodyHtml);
    setBodyEditorRevision((rev) => rev + 1);
    setKeptAttachments(template.attachments ?? []);
    setNewFiles([]);
    lastFocusedFieldRef.current = 'body';
    subjectSelectionRef.current = {
      start: template.subjectLine.length,
      end: template.subjectLine.length,
    };
  }, [template, open]);

  const mergeTags = template?.mergeTags ?? [];
  const isBusy = isSaving || isResetting || isPreviewing;

  const attachmentSummary = useMemo(() => {
    return [
      ...keptAttachments.map((a) => a.fileName),
      ...newFiles.map((f) => f.name),
    ];
  }, [keptAttachments, newFiles]);

  const syncSubjectSelection = () => {
    const input = subjectInputRef.current;
    if (!input) return;
    subjectSelectionRef.current = {
      start: input.selectionStart ?? input.value.length,
      end: input.selectionEnd ?? input.value.length,
    };
  };

  const insertMergeTagIntoSubject = (token: string) => {
    const input = subjectInputRef.current;
    const { start, end } = subjectSelectionRef.current;
    const next = subjectLine.slice(0, start) + token + subjectLine.slice(end);
    setSubjectLine(next);
    subjectSelectionRef.current = {
      start: start + token.length,
      end: start + token.length,
    };
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(
        subjectSelectionRef.current.start,
        subjectSelectionRef.current.end,
      );
    });
  };

  const insertMergeTag = (tag: string) => {
    const token = `{{${tag}}}`;

    if (lastFocusedFieldRef.current === 'subject') {
      insertMergeTagIntoSubject(token);
      toast.success(`Inserted {{${tag}}}`);
      return;
    }

    const editor = bodyEditorRef.current?.editor;
    if (editor && !editor.isDestroyed) {
      editor.chain().focus().insertContent(token).run();
      toast.success(`Inserted {{${tag}}}`);
      return;
    }

    setBodyHtml((prev) => `${prev}${prev.endsWith('>') ? '' : ' '}${token}`);
    toast.success(`Inserted {{${tag}}}`);
  };

  const undoBodyEdit = () => {
    const editor = bodyEditorRef.current?.editor;
    if (!editor || editor.isDestroyed || !editor.can().undo()) {
      return;
    }
    editor.chain().focus().undo().run();
  };

  const handleReset = async () => {
    const confirmed = window.confirm(
      'Reset this email to the system default? Your custom subject, body, and PDF attachments will be removed.',
    );
    if (!confirmed) return;
    try {
      await resetTemplate(emailType);
      toast.success('Template reset to system default');
      void refetch();
    } catch (error) {
      console.error(error);
      toast.error('Failed to reset template');
    }
  };

  const handleSave = async () => {
    if (!subjectLine.trim()) {
      toast.error('Subject line is required');
      return;
    }
    if (isBlankEmailHtml(bodyHtml)) {
      toast.error('Email body is required');
      return;
    }
    try {
      await upsertTemplate({
        emailType,
        subjectLine,
        bodyHtml,
        keepAttachmentPaths: keptAttachments.map((a) => a.gcsPath),
        attachments: newFiles,
      });
      toast.success('Email template saved');
      void refetch();
    } catch (error) {
      console.error(error);
      showEmailTemplateSaveError({
        detail: getErrorMessage(error, 'Failed to save email template'),
        onUndo: undoBodyEdit,
        onReset: () => {
          void handleReset();
        },
      });
    }
  };

  const handlePreview = async () => {
    try {
      const preview = await previewTemplate({
        emailType,
        subjectLine,
        bodyHtml,
      });
      setPreviewSubject(preview.subject);
      setPreviewHtml(preview.html);
      setPreviewOpen(true);
    } catch (error) {
      console.error(error);
      toast.error('Failed to render preview');
    }
  };

  const handleFilePick = (files: FileList | null) => {
    if (!files?.length) return;
    const pdfs = Array.from(files).filter(
      (f) =>
        f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'),
    );
    if (pdfs.length !== files.length) {
      toast.error('Only PDF files can be attached');
    }
    const total = keptAttachments.length + newFiles.length + pdfs.length;
    if (total > 5) {
      toast.error('A maximum of 5 PDF attachments is allowed');
      return;
    }
    for (const file of pdfs) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds the 10MB limit`);
        return;
      }
    }
    setNewFiles((prev) => [...prev, ...pdfs]);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className='flex max-h-[92vh] w-full max-w-5xl flex-col gap-0 overflow-hidden p-0'>
          <DialogHeader className='border-b px-6 py-4'>
            <DialogTitle>Edit {scenarioName}</DialogTitle>
            <DialogDescription>
              Customize the subject and body for this system email. The editor
              opens with the same template parents currently receive. Saving
              creates a studio-specific override. Use merge tags to personalize
              content.
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className='flex justify-center py-16'>
              <Loader2 className='size-8 animate-spin text-muted-foreground' />
            </div>
          ) : isError ? (
            <div className='flex flex-col items-center gap-3 px-6 py-16 text-center'>
              <div className='flex size-12 items-center justify-center rounded-full bg-muted'>
                <AlertCircle className='size-6 text-muted-foreground' />
              </div>
              <p className='text-base font-semibold text-foreground'>
                Email template could not load
              </p>
              <p className='mx-auto max-w-md text-sm text-muted-foreground'>
                Refresh and try again. If the issue continues, contact support.
                Actual emails are unaffected.
              </p>
            </div>
          ) : (
            <div className='grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[1fr_240px]'>
              <ScrollArea className='max-h-[calc(92vh-10rem)] px-6 py-4'>
                <div className='space-y-4 pb-4'>
                  <div className='flex items-center gap-2'>
                    {template?.isCustom ? (
                      <Badge variant='secondary'>Customized</Badge>
                    ) : (
                      <Badge variant='outline'>System default</Badge>
                    )}
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='system-email-subject'>Subject line</Label>
                    <Input
                      id='system-email-subject'
                      ref={subjectInputRef}
                      value={subjectLine}
                      onChange={(e) => {
                        setSubjectLine(e.target.value);
                        syncSubjectSelection();
                      }}
                      onFocus={() => {
                        lastFocusedFieldRef.current = 'subject';
                        syncSubjectSelection();
                      }}
                      onSelect={syncSubjectSelection}
                      onKeyUp={syncSubjectSelection}
                      onClick={syncSubjectSelection}
                      placeholder='e.g. Payment Receipt — {{studio_name}}'
                    />
                    <p className='text-xs text-muted-foreground'>
                      Focus this field, then click a merge tag to insert it into
                      the subject.
                    </p>
                  </div>

                  <div className='space-y-2'>
                    <Label>Body</Label>
                    <div
                      className='rounded-md border border-border'
                      onFocusCapture={() => {
                        lastFocusedFieldRef.current = 'body';
                      }}
                    >
                      {bodyEditorSeed !== null ? (
                        <TextEditor
                          key={`${emailType}-${bodyEditorRevision}`}
                          ref={bodyEditorRef}
                          content={bodyEditorSeed}
                          onChangeContent={(val: string) => setBodyHtml(val)}
                        />
                      ) : null}
                    </div>
                  </div>

                  <div className='space-y-2'>
                    <div className='flex items-center justify-between gap-2'>
                      <Label>Attachments</Label>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Paperclip className='mr-1.5 size-3.5' />
                        Add PDF
                      </Button>
                    </div>
                    <p className='text-xs text-muted-foreground'>
                      Optional. Up to 5 PDFs, 10MB each.
                    </p>
                    {attachmentSummary.length > 0 && (
                      <ul className='space-y-1'>
                        {keptAttachments.map((attachment) => (
                          <li
                            key={attachment.gcsPath}
                            className='flex items-center justify-between rounded-md border px-3 py-2 text-sm'
                          >
                            <span className='flex items-center gap-2 truncate'>
                              <FileText className='size-4 shrink-0 text-muted-foreground' />
                              {attachment.fileName}
                            </span>
                            <Button
                              type='button'
                              variant='ghost'
                              size='icon'
                              className='size-7'
                              onClick={() =>
                                setKeptAttachments((prev) =>
                                  prev.filter(
                                    (a) => a.gcsPath !== attachment.gcsPath,
                                  ),
                                )
                              }
                            >
                              <X className='size-4' />
                            </Button>
                          </li>
                        ))}
                        {newFiles.map((file, index) => (
                          <li
                            key={`${file.name}-${index}`}
                            className='flex items-center justify-between rounded-md border border-dashed px-3 py-2 text-sm'
                          >
                            <span className='flex items-center gap-2 truncate'>
                              <FileText className='size-4 shrink-0 text-muted-foreground' />
                              {file.name}{' '}
                              <span className='text-xs text-muted-foreground'>
                                (new)
                              </span>
                            </span>
                            <Button
                              type='button'
                              variant='ghost'
                              size='icon'
                              className='size-7'
                              onClick={() =>
                                setNewFiles((prev) =>
                                  prev.filter((_, i) => i !== index),
                                )
                              }
                            >
                              <X className='size-4' />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <input
                      ref={fileInputRef}
                      type='file'
                      accept='application/pdf,.pdf'
                      multiple
                      className='hidden'
                      onChange={(e) => {
                        handleFilePick(e.target.files);
                        e.target.value = '';
                      }}
                    />
                  </div>
                </div>
              </ScrollArea>

              <aside className='border-t bg-muted/20 lg:border-l lg:border-t-0'>
                <div className='px-4 py-3'>
                  <p className='text-sm font-medium'>Merge tags</p>
                  <p className='mt-1 text-xs text-muted-foreground'>
                    Click to insert into the focused field (subject or body).
                  </p>
                </div>
                <Separator />
                <ScrollArea className='h-[min(420px,50vh)] px-3 py-2'>
                  <div className='space-y-1 pb-4'>
                    {mergeTags.map((tag) => (
                      <button
                        key={tag.tag}
                        type='button'
                        className='flex w-full flex-col items-start rounded-md px-2 py-2 text-left hover:bg-muted'
                        // Keep subject/body selection; don't steal focus on mousedown.
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insertMergeTag(tag.tag)}
                      >
                        <span className='font-mono text-xs text-foreground'>
                          {`{{${tag.tag}}}`}
                        </span>
                        <span className='text-[11px] text-muted-foreground'>
                          {tag.description}
                        </span>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </aside>
            </div>
          )}

          <DialogFooter className='gap-2 border-t px-6 py-4 sm:justify-between'>
            <Button
              type='button'
              variant='ghost'
              disabled={isBusy || isLoading || !template?.isCustom}
              onClick={() => void handleReset()}
            >
              {isResetting ? (
                <Loader2 className='mr-2 size-4 animate-spin' />
              ) : (
                <RotateCcw className='mr-2 size-4' />
              )}
              Reset to default
            </Button>
            <div className='flex flex-wrap gap-2'>
              <Button
                type='button'
                variant='outline'
                disabled={isBusy || isLoading}
                onClick={() => void handlePreview()}
              >
                {isPreviewing ? (
                  <Loader2 className='mr-2 size-4 animate-spin' />
                ) : (
                  <Eye className='mr-2 size-4' />
                )}
                Preview
              </Button>
              <Button
                type='button'
                disabled={isBusy || isLoading}
                onClick={() => void handleSave()}
              >
                {isSaving ? (
                  <Loader2 className='mr-2 size-4 animate-spin' />
                ) : (
                  <Save className='mr-2 size-4' />
                )}
                Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className='max-h-[90vh] w-full max-w-3xl overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Preview — {scenarioName}</DialogTitle>
            <DialogDescription>
              Sample data is used for merge tags.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            <div className='rounded-lg border border-border bg-muted/30 px-4 py-3'>
              <p className='text-sm font-medium text-muted-foreground'>
                Subject
              </p>
              <p className='text-base font-semibold'>{previewSubject}</p>
            </div>
            <div className='rounded-xl border border-border bg-white p-2 shadow-sm sm:p-4'>
              <iframe
                title={`${scenarioName} custom preview`}
                srcDoc={previewHtml}
                sandbox=''
                className='mx-auto h-[560px] w-full max-w-screen-sm rounded-lg border-0 bg-white'
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
